<template>
  <div id="app">
    <AppHeader v-if="isAuthenticated" />
    <SyncStatusBar v-if="isAuthenticated" />
    <main class="main-content" :class="{ 'with-sync-bar': showSyncBar }">
      <router-view />
    </main>
    <AppFooter v-if="showFooter" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRoute } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import SyncStatusBar from '@/components/SyncStatusBar.vue'

const authStore = useAuthStore()
const route = useRoute()

const windowWidth = ref(window.innerWidth)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const showSyncBar = computed(() => authStore.isAuthenticated && authStore.isStravaConnected && !authStore.isStravaSynced)

// Hide footer on MapView for mobile devices
const showFooter = computed(() => { // eslint-disable-line no-unused-vars
  if (!isAuthenticated.value) return false
  if (route.name === 'Map') {
    return windowWidth.value >= 768 // Show footer only on desktop
  }
  return true
})

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style>
/* CSS Custom Properties - Color Palette */
:root {
--primary-color: #6588b3;           /* Główny niebieski */
--primary-hover: #4c6986;           /* Ciemniejszy niebieski */
--primary-gradient-start: #6588b3;
--primary-gradient-end: #415971;    /* Najciemniejszy niebieski */

--secondary-color: #415971;          /* Ciemny niebiesko-szary */
--secondary-hover: #537291;          /* Średni niebieski */
--secondary-gradient-start: #dfc4b9;
--secondary-gradient-end: #f7b599;

--text-primary: #2b3b4e;             /* Średni niebieski */
--text-secondary: #4c6986;           /* Jasny szary */
--text-muted: #88a6c0;                 /* Bardzo jasny niebieski */
--text-light: #e9c4b4;               /* Ciepły różowawy */

--bg-light: #e0ecf5;                 /* Bardzo jasny niebieski */
--bg-light-accent: #b4b9cd;          /* Jasny szary */
--bg-success: #e9c4b4;               /* Ciepły różowawy */
--bg-white: #FFFFFF;

--chart-primary: #6588b3;
--chart-secondary: #88a6c0;
--chart-tertiary: #415971;
--chart-point-bg: #FFFFFF;

--btn-google: #4285F4;
--btn-google-hover: #3367D6;

--btn-strava: #FC4C02;
--btn-strava-hover: #E84300;

--error-color: #D32F2F;
--error-text: #B71C1C;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  transition: padding-top 0.3s ease;
}

.main-content.with-sync-bar {
  /* Usunięto padding-top bo sync bar już nie jest fixed */
}

@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }

  .main-content.with-sync-bar {
    /* Usunięto padding-top bo sync bar już nie jest fixed */
  }
}
</style>
